<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useQuery, useMutation } from '@vue/apollo-composable';
import {
  BucketsSamplesDocument,
  BucketType,
  ProjectSampleAssociationCreateDocument,
  ProjectSampleAssociationNewFragmentDoc,
  ProjectSampleAssociationType,
  SampleType,
} from '@/gql/graphql';
import {
  BAlert,
  BAccordion,
  BAccordionItem,
  BButton,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { BUCKET_DATA_TYPES, PAGING_ITEMS_PER_PAGE } from '@/constants';
import { deepParseDates } from '@/utils/date-utils';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  projectId: number;
  projectName: string;
}>();

// --- Component state ---
const batchOperationInProgress = ref<boolean>(false);
const batchOperationShowSuccess = ref<boolean>(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return qResult.value?.buckets?.total || 0;
});
const bucketFriendlyNameSearchPattern = ref<string>('');
const debouncedFilter = refDebounced(bucketFriendlyNameSearchPattern, 400);

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

const show = computed<boolean>(() => {
  return props.show;
});

// --- Query User Samples by Buckets ---
const qVars = computed(() => ({
  dataType: BUCKET_DATA_TYPES.INPUT,
  friendlyNamePattern: debouncedFilter.value || '',
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: PAGING_ITEMS_PER_PAGE,
}));

const {
  result: qResult,
  loading: qInProgress,
  error: qError,
  refetch: refetchBuckets,
} = useQuery(BucketsSamplesDocument, qVars, {
  fetchPolicy: 'network-only',
  enabled: show,
});

watch(pagingPageCurrent, () => {
  refetchBuckets(qVars.value);
});

watch(debouncedFilter, () => {
  pagingPageCurrent.value = 1; // reset paging when the search filter changes
});

// --- List of Buckets ---
const buckets = computed<BucketType[]>(() => {
  const rawList = qResult.value?.buckets?.buckets || [];
  const fineList = rawList
    .filter((b) => b !== null)
    .map((b) => deepParseDates(b) as BucketType);
  console.debug('buckets query result updated, count=', fineList.length);
  return fineList;
});

// --- Sample Table Fields ---
const sampleTableFields = computed(() => [
  {
    key: 'name',
    label: 'Sample Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
]);

// --- Mutation: Create Project-Sample Association ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);
const mutationsSuccessful = ref(0);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectSampleAssociationCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectSampleAssociationCreate) {
    console.debug('Project-sample association created successfully');
    mutationShowSuccess.value = true;
    mutationsSuccessful.value++;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Project-sample association failed: ' + res.data;
    console.error('Project-sample association failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error creating project-user association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function sampleAlreadyAssociated(sample: SampleType): boolean {
  return (
    sample.projectsAssociated !== undefined &&
    sample.projectsAssociated !== null &&
    sample.projectsAssociated?.some(
      (assoc) => assoc && assoc.projectId === props.projectId
    )
  );
}

async function selectAllSamples(samples: SampleType[]) {
  if (samples.length === 0) {
    return;
  }
  batchOperationInProgress.value = true;
  for (const sample of samples) {
    if (!sampleAlreadyAssociated(sample)) {
      // wait for previous mutation to finish
      while (mutationInProgress.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      // initiate new mutation
      onProjectSampleAssociationRequested(sample);
    }
  }
  batchOperationShowSuccess.value = true;
  setTimeout(() => {
    batchOperationShowSuccess.value = false;
  }, 3000);
  batchOperationInProgress.value = false;
}

function onProjectSampleAssociationRequested(sample: SampleType) {
  if (!sample.id) {
    mutationError.value = 'Sample not provided';
    console.error('Sample not provided');
    return;
  }

  if (mutationInProgress.value) {
    console.warn('Mutation already in progress, please wait');
    return;
  }

  // Prevent duplicate associations
  if (sampleAlreadyAssociated(sample)) {
    console.error('Sample is already associated with this project');
    return;
  }

  // Continue with association creation
  console.debug(
    'Creating project-sample association, project ',
    props.projectId,
    'sample',
    sample.id
  );

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation(
    { projectId: props.projectId, sampleId: sample.id },
    {
      update(cache, { data }) {
        // Find the assoc returned by the mutation (server or optimistic)
        const assoc =
          data?.projectSampleAssociationCreate?.samplesAssociated?.find(
            (a) => a?.sample?.id === sample.id
          );

        if (!assoc || !assoc.id) {
          console.warn(
            'Association or its ID not returned by mutation, cannot update cache'
          );
          return;
        }

        // Build the sample entity id
        const sampleEntityId = cache.identify({
          __typename: 'SampleType',
          id: sample.id,
        });
        if (!sampleEntityId) {
          console.warn(
            'Could not identify sample in cache, cannot update cache'
          );
          return;
        }

        // Build the association entity id
        const assocEntityId = cache.identify({
          __typename: 'ProjectSampleAssociationType',
          id: assoc.id, // or { projectId, sampleId } if using composite keyFields
        });
        if (!assocEntityId) {
          console.warn(
            'Could not identify project-sample association in cache, cannot update cache'
          );
          return;
        }

        // Ensure the association entity exists in cache (minimal fields)
        const newFragmentData: ProjectSampleAssociationType = {
          __typename: 'ProjectSampleAssociationType',
          id: assoc.id,
          projectId: props.projectId,
          sampleId: sample.id,
        };

        const assocRef = cache.writeFragment({
          id: assocEntityId, // <-- write to the association entity, NOT the sample
          fragment: ProjectSampleAssociationNewFragmentDoc, // must be ON ProjectSampleAssociationType
          data: newFragmentData,
        });

        // Append the association reference to Sample.projectsAssociated
        cache.modify({
          id: sampleEntityId,
          fields: {
            projectsAssociated(existingRefs = [], { readField }) {
              const exists = existingRefs.some(
                (ref: any) => readField('projectId', ref) === props.projectId
              );
              return exists ? existingRefs : [...existingRefs, assocRef];
            },
          },
        });
      },
    }
  );
}

function closeModal() {
  if (mutationsSuccessful.value > 0) {
    emits('closeAndRefresh');
  } else {
    emits('close');
  }
}
</script>
<template>
  <div>
    <!-- MODAL: Create Project-Sample Association -->
    <b-modal v-model="show" @hide="closeModal">
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="plus" class="me-2" />
        Add samples to project
        <strong>{{ props.projectName }}</strong>
      </template>

      <!-- Modal Content -->
      <div>
        <!-- Buckets Search Filter Form -->
        <b-form-input
          v-model="bucketFriendlyNameSearchPattern"
          type="search"
          placeholder="Filter data by name..."
          class="mb-3"
        />

        <!-- Paging (above) -->
        <BPagination
          v-if="pagingTotalRows > PAGING_ITEMS_PER_PAGE"
          v-model="pagingPageCurrent"
          :total-rows="pagingTotalRows"
          :per-page="PAGING_ITEMS_PER_PAGE"
          pills
          size="sm"
          class="mb-2"
        />

        <!-- Browse Containers -->
        <div
          v-if="qInProgress"
          class="d-flex align-items-center justify-content-center text-center"
          style="min-height: 200px"
        >
          <BSpinner label="Loading data" />
        </div>

        <!-- After the first load, never unmount the list -->
        <div v-if="!qInProgress">
          <!-- No Buckets Found -->
          <div v-if="!buckets.length">
            <h5 class="my-5 py-5 text-center">
              <span v-if="debouncedFilter">
                No data found matching the filter criteria.
              </span>
              <span v-else> There are no data in your collection. </span>
            </h5>
          </div>

          <!-- Show Buckets List -->
          <BAccordion v-if="buckets.length > 0">
            <BAccordionItem
              v-for="bucket in buckets"
              :key="bucket.id"
              :id="`bucket-${bucket.id}`"
              button-class="accordion-title"
            >
              <template #title>
                <span>
                  <font-awesome-icon icon="database" class="me-2" />
                  {{ bucket.friendlyName }}
                </span>
              </template>
              <template v-if="bucket.samples && bucket.samples.length > 0">
                <!-- Batch operation - select all -->
                <div class="d-flex justify-content-end mb-2">
                  <b-button
                    :disabled="
                      mutationInProgress ||
                      batchOperationInProgress ||
                      batchOperationShowSuccess
                    "
                    variant="outline-primary"
                    size="sm"
                    @click="selectAllSamples(bucket.samples as SampleType[])"
                  >
                    <span v-if="batchOperationInProgress">
                      Sample association
                      <b-spinner small class="ms-2" />
                    </span>
                    <span v-else-if="batchOperationShowSuccess">
                      Success
                      <font-awesome-icon
                        :icon="['fas', 'check-double']"
                        class="ms-2"
                      />
                    </span>
                    <span v-else> Select All </span>
                  </b-button>
                </div>
                <!-- List Samples in Table -->
                <b-table
                  :items="bucket.samples"
                  :fields="sampleTableFields"
                  primary-key="id"
                  small
                  responsive
                  hover
                  class="browser-table"
                >
                  <!-- Sample Name Column -->
                  <template #cell(name)="data">
                    <font-awesome-icon icon="flask" class="me-2" />
                    {{ data.item.name }}
                  </template>

                  <!-- Actions Column (aligned to right) -->
                  <template #cell(actions)="data">
                    <div class="text-end">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="onProjectSampleAssociationRequested(data.item)"
                        :disabled="sampleAlreadyAssociated(data.item)"
                      >
                        <font-awesome-icon
                          :icon="
                            sampleAlreadyAssociated(data.item)
                              ? 'check'
                              : 'plus'
                          "
                        />
                      </b-button>
                    </div>
                  </template>
                </b-table>
              </template>
              <template v-else>
                <small> There are no samples in this bucket. </small>
              </template>
            </BAccordionItem>
          </BAccordion>
        </div>

        <!-- Paging (below) -->
        <BPagination
          v-if="pagingTotalRows > PAGING_ITEMS_PER_PAGE && !qInProgress"
          v-model="pagingPageCurrent"
          :total-rows="pagingTotalRows"
          :per-page="PAGING_ITEMS_PER_PAGE"
          pills
          size="sm"
          class="mt-2"
        />

        <!-- Error Fetching User Samples -->
        <BAlert v-model="qError" variant="danger">
          {{ qError }}
        </BAlert>

        <!-- Error Mutation -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="closeModal"> Close </b-button>
      </template>
    </b-modal>
  </div>
</template>
